import React, { useState, useEffect, useMemo, useCallback, useRef, useId } from 'react';
import { toast } from '../../../../utils/toast';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ArrowRight,
  PackageOpen,
  Truck,
  Plus,
  X,
  AlertTriangle,
  Search,
  Building2,
  CheckCheck,
  Eraser,
  Undo2,
  Info,
  Layers,
  Network,
} from 'lucide-react';
import useAuth from '../../../../hooks/useAuth';
import { getActiveClientsApi } from '../../../../api/company';
import { getBillableCompanies } from '../../../../api/cobro';
import useCobroStore from '../../../../store/cobro';
import { Button } from '../../../../components/ui/Button';
import { Select } from '../../../../components/ui/Select';
import { Input } from '../../../../components/ui/Input';
import { Modal } from '../../../../components/ui/Modal';
import { Skeleton } from '../../../../components/ui/Skeleton';
import {
  CHARGE_MODES,
  CHARGE_MODE_LABELS,
  DATE_PRESETS,
  rangeFromPreset,
  detectCycle,
  scaleFitsCycle,
  formatCurrency,
  resolveOperatorCurrency,
} from '../utils/cobroHelpers';
import { getCommerceScales } from '../../../../api/billing';
import { apiErrorMessage } from '../../../../utils/apiErrorMessage';
import {
  commerceSecondaryLine,
  matchCommerce,
  toCommerceOption,
} from '../../../../utils/commerceSearch';
import {
  UNIT_COMPANY,
  UNIT_HOLDING,
  consolidatedHoldings,
  holdingCodeText,
  holdingErrorText,
  holdingMemberIds,
  holdingSkipReasonText,
  holdingsMissingFromPreview,
  toHoldingOption,
  unansweredHoldingIds,
  unitKey,
} from '../utils/holdings';
import { apiErrorStatus } from '../utils/collectionReminders';
import { loadHoldingsWithConsolidation } from '../utils/operatorHoldingsWithConsolidation';
import HoldingPreviewCard from './holdings/HoldingPreviewCard';

const todayYMD = () => new Date().toISOString().split('T')[0];

// Los pedidos devueltos (estados 21, 22, 31 y 41) no tienen opción en el formulario:
// billing los descuenta siempre y en los dos modos, e ignora el viejo `discountReturned`.
//
// `selection` son las unidades a cobrar: comercios sueltos ({ kind: 'company' }) y holdings
// ({ kind: 'holding' }), cada una con su `value` (id) y `label`. Viajan como `companyIds` y
// `holdingIds`.
const EMPTY = {
  selection: [],
  chargeMode: 'recolectado',
  preset: 'previous_month',
  periodStart: '',
  periodEnd: '',
  unpricedFallbackPrice: '',
  adjustments: [],
  notes: '',
};

// Orden del wizard: primero el qué (periodo + modo), luego el a quién (comercios).
const STEPS = ['Periodo', 'Comercios', 'Vista previa', 'Confirmar'];

// Espejo de MODE_GAP_LOOKBACK_DAYS de microservice-billing (cobro/cobro.service.ts): cuántos
// días antes del período mira el aviso de "hueco" (`modeWarnings.unbilledBeforePeriod`). La
// respuesta no lo trae y acá solo se usa para explicarlo en el texto.
const MODE_GAP_LOOKBACK_DAYS = 180;

const MODE_ICON = { recolectado: PackageOpen, entregado: Truck };

// Por qué billing omitió un comercio, en el idioma de la UI. Un código que este front no
// conozca se muestra tal cual: mejor crudo que una omisión sin explicación.
// `no_active_contract` ya no lo emite billing (ahora tarifa con el último contrato o la base
// del operador) pero se conserva para un billing anterior.
const SKIPPED_REASON_LABELS = {
  no_active_contract: 'sin contrato activo',
  no_contract_unconfirmed: 'sin contrato, cobro con tarifa base no confirmado',
  cycle_mismatch: 'fuera de su ciclo de cobro',
  no_packages: 'sin entregas en el período',
  no_priced_packages: 'sin entregas tarifadas',
};

// Con qué contrato tarifaría billing a cada comercio (`contractSource`):
//   · in_effect: un contrato en vigor que cubre el período (sin aviso);
//   · latest: ninguno en vigor → el último contrato del par, vencido o inactivo;
//   · none: ningún contrato con el operador → su tarifa base.
// Rótulo (clave i18n) y tono del aviso, en el paso 2 (comercios con pedidos por cobrar) y en
// el 3 (vista previa). Un billing anterior no manda `contractSource` en la vista previa: ahí
// manda `hasContract`.
const listContractBadge = (info) => {
  if (info?.contractSource === 'none') return { tone: 'error', label: 'sin contrato con el operador' };
  if (info?.contractSource !== 'latest') return null;
  return info.contractStatus === 'inactive'
    ? { tone: 'warning', label: 'contrato inactivo' }
    : { tone: 'warning', label: 'contrato no vigente' };
};

const previewContractBadge = (c) => {
  if (c.contractSource === 'in_effect') return null;
  if (c.contractSource === 'none') return { tone: 'error', label: 'sin contrato · tarifa base' };
  if (c.contractSource === 'latest') {
    return c.contractStatus === 'inactive'
      ? { tone: 'warning', label: 'contrato inactivo · tarifa del último contrato' }
      : { tone: 'warning', label: 'contrato no vigente · tarifa del último contrato' };
  }
  return c.hasContract ? null : { tone: 'error', label: 'sin contrato' };
};

// Comercios con pedidos por cobrar del período (GET billing/cobro/billable-companies). `key`
// = rango + modo pedidos: un resultado de otra clave no se usa. `code` = el del error de billing.
const NO_BILLABLE = { key: null, status: 'idle', items: [], code: null };
const NO_ITEMS = [];
// Errores de billable-companies por el PERÍODO (400: más de 62 días o fechas inválidas):
// reintentar falla igual, hay que elegir otro período.
const BILLABLE_PERIOD_ERROR_CODES = new Set(['period_too_long', 'invalid_period']);

// Comercios que se nombran por motivo antes de resumir el resto con "y N más".
const MAX_NAMES_PER_REASON = 5;

/**
 * Agrupa los comercios omitidos por motivo, conservando el orden en que aparecen en
 * `skipped`. Devuelve un string por grupo: "sin contrato activo: Comercio Uno, Comercio Dos".
 */
const summarizeSkipped = (skipped, t, companyLabel) => {
  const byReason = new Map();
  for (const s of skipped ?? []) {
    const reason = s?.reason ?? '';
    const ids = byReason.get(reason) ?? [];
    ids.push(s?.companyId);
    byReason.set(reason, ids);
  }
  return [...byReason.entries()].map(([reason, companyIds]) => {
    const label = SKIPPED_REASON_LABELS[reason]
      ? t(SKIPPED_REASON_LABELS[reason])
      : reason;
    const shown = companyIds.slice(0, MAX_NAMES_PER_REASON).map((id) => companyLabel(id));
    const rest = companyIds.length - shown.length;
    const names = rest > 0 ? `${shown.join(', ')} ${t('y {{n}} más', { n: rest })}` : shown.join(', ');
    return `${label}: ${names}`;
  });
};

// Holding que billing bloquea porque su nota superaría el máximo de líneas por nota.
const blockedByLineLimit = (h) => Array.isArray(h?.blockers) && h.blockers.includes('too_many_lines');

// Motivos de `skipped` de generate que son un problema de la nota de ESA unidad (no de su
// tarifa ni de sus datos): el reporte los nombra aparte, cada uno con su remedio.
const UNIT_PROBLEM_REASONS = new Set(['too_many_lines', 'emission_failed']);

// Orden canónico de las categorías de sobredimensionado, el mismo que usan el editor de
// tarifas y la configuración de cobro. El backend no garantiza el orden del array, y
// "2XL" antes que "XL" alfabéticamente sería confuso. Categorías desconocidas al final.
const SIZE_CATEGORY_ORDER = ['XL', '2XL', '3XL'];
const bySizeCategory = (a, b) => {
  const ia = SIZE_CATEGORY_ORDER.indexOf(a.category);
  const ib = SIZE_CATEGORY_ORDER.indexOf(b.category);
  if (ia === -1 && ib === -1) return String(a.category).localeCompare(String(b.category));
  if (ia === -1) return 1;
  if (ib === -1) return -1;
  return ia - ib;
};

const GenerateCobroWizard = ({ isOpen, toggle, onGenerated }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const logisticCompanyId = user?.logisticCompany?._id ?? null;
  const currency = resolveOperatorCurrency(user?.logisticCompany);

  const { previewing, preview, runPreview, clearPreview, saving, generate } = useCobroStore((s) => ({
    previewing: s.previewing,
    preview: s.preview,
    runPreview: s.runPreview,
    clearPreview: s.clearPreview,
    saving: s.saving,
    generate: s.generate,
  }));

  const [step, setStep] = useState(1); // 1 periodo+modo · 2 comercios · 3 preview · 4 confirmar
  const [form, setForm] = useState(EMPTY);
  // Base de la lista: comercios con contrato activo (users/companies/active-contracts).
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  // Comercios con pedidos por cobrar del período y modo, con el contrato con que los tarifaría
  // billing. Se unen a la base en cada respuesta (sin acumular entre respuestas). Solo escribe
  // la última lectura; "Reintentar" pide otra.
  const [billable, setBillable] = useState(NO_BILLABLE);
  const [billableRetry, setBillableRetry] = useState(0);
  const billableSeq = useRef(0);
  // Holdings del operador (GET contracts/holdings, con el estado del cobro consolidado de
  // billing) y el estado de su carga. Los ENCENDIDOS deciden qué comercios NO se ofrecen
  // sueltos (los cobra su holding); todos precargan el paso "Datos del holding" de la vista
  // previa y dan nombre a los holdings de las respuestas de billing.
  const [holdings, setHoldings] = useState([]);
  const [holdingsStatus, setHoldingsStatus] = useState('idle'); // loading | ready | error
  const [holdingsError, setHoldingsError] = useState(null);
  const holdingsSeq = useRef(0);
  // `unit` = unitKey de la unidad (comercio u holding) a la que va el ajuste.
  const [adjForm, setAdjForm] = useState({ unit: '', type: 'surcharge', label: '', amount: '' });
  // Se guardaron datos de un holding desde la vista previa (paso "Datos del holding"): lo que
  // muestra ya no es lo que billing emitiría (un holding bloqueado puede haber quedado listo).
  // "Confirmar" espera al próximo cálculo.
  const [previewStale, setPreviewStale] = useState(false);

  // Selector de comercios
  const [companyQuery, setCompanyQuery] = useState('');
  const [onlySelected, setOnlySelected] = useState(false);
  const uncontractedGroupId = useId();

  useEffect(() => {
    if (!isOpen || !logisticCompanyId) return;
    setLoadingClients(true);
    getActiveClientsApi(logisticCompanyId)
      .then((data) => {
        if (Array.isArray(data)) {
          setClients(data.map((c) => toCommerceOption(c, t('Sin nombre'))));
          return;
        }
        // getActiveClientsApi no lanza: devuelve `{ error: body }`. Un operador sin ningún
        // contrato activo recibe 404 y es un caso normal (la lista sale entonces de los
        // comercios con pedidos por cobrar); cualquier otro error se avisa.
        setClients([]);
        if (data?.error?.statusCode !== 404) toast.error(t('No se pudieron cargar los comercios'));
      })
      .catch(() => toast.error(t('No se pudieron cargar los comercios')))
      .finally(() => setLoadingClients(false));
  }, [isOpen, logisticCompanyId, t]);

  // Holdings del operador. Si fallan (contracts, o billing al decir cuáles están encendidos),
  // no se ofrece ninguno y todos los comercios aparecen sueltos: billing omite los de un
  // holding encendido (`belongs_to_holding`) y la vista previa lo informa, así que se avisa en
  // vez de bloquear el cobro de los comercios sueltos. Solo escribe la última lectura
  // ("Reintentar" puede pedir otra mientras una sigue en vuelo).
  const loadHoldings = useCallback(async () => {
    const seq = ++holdingsSeq.current;
    setHoldingsStatus('loading');
    try {
      const { holdings: loaded, consolidationError } = await loadHoldingsWithConsolidation();
      if (seq !== holdingsSeq.current) return;
      // Sin el estado de billing los holdings quedan sin `consolidatedEnabled`: ninguno cuenta
      // como encendido, pero sus nombres y datos fiscales siguen sirviendo.
      setHoldings(loaded);
      if (consolidationError) {
        setHoldingsError(
          holdingCodeText(consolidationError, t) ??
            t('No se pudo consultar qué holdings tienen el cobro consolidado encendido.'),
        );
        setHoldingsStatus('error');
        return;
      }
      setHoldingsError(null);
      setHoldingsStatus('ready');
    } catch (err) {
      if (seq !== holdingsSeq.current) return;
      setHoldingsError(holdingErrorText(err, t, 'No se pudieron cargar los holdings del operador.'));
      setHoldingsStatus('error');
    }
  }, [t]);

  useEffect(() => {
    if (!isOpen) return undefined;
    loadHoldings();
    // Al cerrar, una lectura en vuelo ya no escribe.
    return () => {
      holdingsSeq.current += 1;
    };
  }, [isOpen, loadHoldings]);

  // Escala de tarifa por volumen de cada comercio y de cada holding con tarifa, por unitKey,
  // para filtrar la tanda por ciclo.
  //
  // Un fallo acá NO bloquea el cobro: el mapa queda vacío, todas las unidades se tratan
  // como de precio plano y el wizard se comporta como antes de esta funcionalidad. La
  // autoridad es el backend, que igual omite los que no calzan; esto solo evita ofrecer una
  // tanda que se sabe que va a salir omitida.
  const [scaleByUnit, setScaleByUnit] = useState(new Map());
  // Escala de un comercio SIN contrato (la del operador): billing lo tarifa con ella, así que
  // un comercio sin entrada en `scaleByUnit` se filtra por ciclo con esta. Un billing anterior
  // no la manda (null = como antes, se ofrece en cualquier período).
  const [operatorDefaultScale, setOperatorDefaultScale] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    // `Promise.resolve(...)` envuelve la llamada: si por lo que sea no devolviera una
    // promesa, encadenar `.then` directo tiraría el modal entero de cobro por un dato que
    // es solo informativo.
    Promise.resolve(getCommerceScales())
      .then((res) => {
        const body = res?.data;
        const rows = Array.isArray(body?.data) ? body.data : body;
        if (!Array.isArray(rows)) return;
        // Los holdings van en una clave hermana (`holdings`); un billing anterior no la manda.
        const holdingRows = Array.isArray(body?.holdings) ? body.holdings : [];
        setScaleByUnit(
          new Map([
            ...rows.map((r) => [unitKey({ kind: UNIT_COMPANY, value: String(r.companyId) }), r.scale]),
            ...holdingRows.map((r) => [
              unitKey({ kind: UNIT_HOLDING, value: String(r.holdingId) }),
              r.scale,
            ]),
          ]),
        );
        setOperatorDefaultScale(
          typeof body?.operatorDefaultScale === 'string' ? body.operatorDefaultScale : null,
        );
      })
      .catch(() => {
        setScaleByUnit(new Map());
        setOperatorDefaultScale(null);
      });
  }, [isOpen]);

  const resolvedRange = useMemo(() => {
    if (form.preset === 'custom') return { start: form.periodStart, end: form.periodEnd };
    return rangeFromPreset(form.preset) ?? { start: '', end: '' };
  }, [form.preset, form.periodStart, form.periodEnd]);

  // Ciclo que representa el período elegido: 'weekly', 'monthly' o null (ninguno completo).
  const cycle = useMemo(
    () => detectCycle(resolvedRange.start, resolvedRange.end),
    [resolvedRange.start, resolvedRange.end],
  );

  // Comercios con pedidos por cobrar: se piden al llegar al paso 2 con el período y el modo
  // ya elegidos, y otra vez si cambian. Un fallo no bloquea: la lista queda con los de
  // contrato activo y se avisa.
  const billableKey =
    resolvedRange.start && resolvedRange.end
      ? `${resolvedRange.start}|${resolvedRange.end}|${form.chargeMode}`
      : null;
  const onCompaniesStep = step >= 2;
  useEffect(() => {
    if (!isOpen || !onCompaniesStep || !billableKey) return undefined;
    const seq = ++billableSeq.current;
    setBillable({ key: billableKey, status: 'loading', items: [] });
    // `Promise.resolve(...)`, como con las escalas: un valor que no sea promesa no tira el modal.
    Promise.resolve(
      getBillableCompanies({
        chargeMode: form.chargeMode,
        periodStart: resolvedRange.start,
        periodEnd: resolvedRange.end,
      }),
    )
      .then((res) => {
        if (seq !== billableSeq.current) return;
        const items = res?.data?.items;
        if (!Array.isArray(items)) throw new Error('billable-companies sin items');
        setBillable({
          key: billableKey,
          status: 'ready',
          items: items
            .filter((item) => item?.companyId)
            .map((item) => ({ ...item, companyId: String(item.companyId) })),
        });
      })
      .catch((err) => {
        if (seq !== billableSeq.current) return;
        setBillable({
          key: billableKey,
          status: 'error',
          items: [],
          code: err?.response?.data?.code ?? null,
        });
      });
    // Cualquier re-ejecución (otro período o modo, volver al paso 1, cerrar) invalida la
    // lectura en vuelo.
    return () => {
      billableSeq.current += 1;
    };
  }, [
    isOpen,
    onCompaniesStep,
    billableKey,
    billableRetry,
    form.chargeMode,
    resolvedRange.start,
    resolvedRange.end,
  ]);

  // Solo vale la respuesta de la clave vigente: una de otro período cuenta como "cargando".
  const billableReady = billable.key === billableKey && billable.status === 'ready';
  const billableFailed = billable.key === billableKey && billable.status === 'error';
  // Billing rechazó el período (no es una falla pasajera): el aviso no ofrece reintentar.
  const billablePeriodRejected = billableFailed && BILLABLE_PERIOD_ERROR_CODES.has(billable.code);
  const billableItems = billableReady ? billable.items : NO_ITEMS;
  // Skeleton hasta tener las dos listas: elegir antes dejaría fuera a los recién llegados.
  const companiesLoading = loadingClients || (onCompaniesStep && !billableReady && !billableFailed);
  const billableById = useMemo(
    () => new Map(billableItems.map((item) => [item.companyId, item])),
    [billableItems],
  );
  // Catálogo = base ∪ facturables, recalculado desde la base en cada respuesta. Da también el
  // nombre de un comercio sin contrato activo en la vista previa y en los avisos.
  const catalog = useMemo(() => {
    const byId = new Map(clients.map((c) => [c.value, c]));
    billableItems.forEach((item) => {
      if (!byId.has(item.companyId)) {
        byId.set(
          item.companyId,
          toCommerceOption({ _id: item.companyId, name: item.companyName }, t('Sin nombre')),
        );
      }
    });
    return [...byId.values()];
  }, [clients, billableItems, t]);

  // Unidades que se pueden cobrar: los holdings con el cobro consolidado ENCENDIDO y los
  // comercios que no pertenecen a ninguno de ellos. Una empresa de un holding encendido no se
  // cobra sola: la absorbe la nota consolidada de su holding (billing la omitiría con
  // `belongs_to_holding`). Las de un holding apagado se cobran sueltas, como cualquier comercio.
  const enabledHoldings = useMemo(() => consolidatedHoldings(holdings), [holdings]);
  const absorbedIds = useMemo(() => holdingMemberIds(enabledHoldings), [enabledHoldings]);
  const holdingUnits = useMemo(
    () => enabledHoldings.map((h) => toHoldingOption(h, t('Sin nombre'))),
    [enabledHoldings, t],
  );
  const companyUnits = useMemo(
    () =>
      catalog
        .filter((c) => !absorbedIds.has(String(c.value)))
        .map((c) => ({ ...c, kind: UNIT_COMPANY })),
    [catalog, absorbedIds],
  );
  const units = useMemo(() => [...holdingUnits, ...companyUnits], [holdingUnits, companyUnits]);
  const absorbedCount = catalog.length - companyUnits.length;
  // Qué empresas del catálogo se cobran dentro de cada holding encendido: el paso 2 lo explica
  // para que no parezcan desaparecidas.
  const absorbedGroups = useMemo(() => {
    const labelById = new Map(catalog.map((c) => [String(c.value), c.label]));
    return enabledHoldings
      .map((h) => ({
        id: String(h.id),
        name: h.name || t('Sin nombre'),
        members: (h.companies ?? [])
          .map((c) => labelById.get(String(c?.id)))
          .filter(Boolean),
      }))
      .filter((group) => group.members.length > 0);
  }, [enabledHoldings, catalog, t]);
  // Holdings apagados: no se ofrecen y sus empresas figuran como comercios sueltos. Solo se
  // sabe si billing respondió el estado.
  const disabledHoldingCount =
    holdingsStatus === 'ready' ? holdings.length - enabledHoldings.length : 0;

  // Escala con la que billing tarifaría la unidad: la suya, o para un comercio sin entrada (sin
  // contrato) la del operador. Un holding sin tarifa no cae a la del operador.
  const scaleOf = useCallback(
    (u) =>
      scaleByUnit.get(unitKey(u)) ?? (u.kind === UNIT_COMPANY ? operatorDefaultScale : undefined),
    [scaleByUnit, operatorDefaultScale],
  );

  // Unidades facturables en ESTE período: las de precio plano siempre, y las escalonadas
  // solo si el período es exactamente su ciclo.
  const eligibleUnits = useMemo(
    () => units.filter((u) => scaleFitsCycle(scaleOf(u), cycle)),
    [units, scaleOf, cycle],
  );
  const hiddenByCycle = units.length - eligibleUnits.length;

  // Contrato con que billing tarifaría al comercio, según billable-companies (null si no vino).
  const contractInfoOf = useCallback(
    (u) => (u.kind === UNIT_COMPANY ? billableById.get(String(u.value)) ?? null : null),
    [billableById],
  );
  // Sin ningún contrato con el operador: se cobran con su tarifa base, así que no entran en
  // "Seleccionar todos" y se eligen uno a uno.
  const isUncontracted = useCallback(
    (u) => contractInfoOf(u)?.contractSource === 'none',
    [contractInfoOf],
  );

  const reset = () => {
    setStep(1);
    setForm(EMPTY);
    setBillable(NO_BILLABLE);
    setAdjForm({ unit: '', type: 'surcharge', label: '', amount: '' });
    setCompanyQuery('');
    setOnlySelected(false);
    setPreviewStale(false);
    clearPreview();
  };

  const close = () => {
    reset();
    toggle();
  };

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  // ── Selección de comercios y holdings ──
  const selectedKeys = useMemo(() => new Set(form.selection.map(unitKey)), [form.selection]);
  const selectedHoldingCount = form.selection.filter((u) => u.kind === UNIT_HOLDING).length;

  // Mismo matcher que el filtro de comercio de la lista: nombre, razón social o RUT, también
  // para los holdings.
  const visibleUnits = useMemo(
    () =>
      eligibleUnits.filter(
        (u) => matchCommerce(u, companyQuery) && (!onlySelected || selectedKeys.has(unitKey(u))),
      ),
    [eligibleUnits, companyQuery, onlySelected, selectedKeys],
  );

  // Al cambiar el período, sacar de la selección lo que dejó de calzar. Si no, una
  // selección hecha con otro rango viaja al preview y vuelve como `cycle_mismatch` sin que el
  // operador entienda de dónde salió ese comercio u holding.
  useEffect(() => {
    setForm((f) => {
      const kept = f.selection.filter((u) => scaleFitsCycle(scaleOf(u), cycle));
      return kept.length === f.selection.length ? f : { ...f, selection: kept };
    });
  }, [cycle, scaleOf]);

  // Tras cada respuesta EXITOSA de billable-companies, sacar de la selección los comercios
  // que ya no están ni en la base ni en los facturables del período (se eligieron con otro
  // período): viajarían al preview con 0 paquetes. Si la consulta falla, no se poda.
  useEffect(() => {
    if (!billableReady || loadingClients) return;
    const available = new Set([
      ...clients.map((c) => String(c.value)),
      ...billableItems.map((item) => item.companyId),
    ]);
    setForm((f) => {
      const kept = f.selection.filter(
        (u) => u.kind !== UNIT_COMPANY || available.has(String(u.value)),
      );
      return kept.length === f.selection.length ? f : { ...f, selection: kept };
    });
  }, [billableReady, billableItems, clients, loadingClients]);

  // Lo mismo con las empresas que resultan ser de un holding (los holdings llegaron después
  // de elegirlas, o se reintentó su carga): billing las omitiría con `belongs_to_holding`.
  useEffect(() => {
    if (absorbedIds.size === 0) return;
    setForm((f) => {
      const kept = f.selection.filter(
        (u) => u.kind !== UNIT_COMPANY || !absorbedIds.has(String(u.value)),
      );
      return kept.length === f.selection.length ? f : { ...f, selection: kept };
    });
  }, [absorbedIds]);

  // "Seleccionar todos/visibles" opera sobre los visibles CON contrato; los sin contrato van en
  // su propio grupo, al final.
  const selectableVisible = useMemo(
    () => visibleUnits.filter((u) => !isUncontracted(u)),
    [visibleUnits, isUncontracted],
  );
  const uncontractedVisible = useMemo(
    () => visibleUnits.filter(isUncontracted),
    [visibleUnits, isUncontracted],
  );
  const selectableEligibleCount = useMemo(
    () => eligibleUnits.filter((u) => !isUncontracted(u)).length,
    [eligibleUnits, isUncontracted],
  );
  const allVisibleSelected =
    selectableVisible.length > 0 && selectableVisible.every((u) => selectedKeys.has(unitKey(u)));

  const toggleUnit = (unit) =>
    setForm((f) => {
      const key = unitKey(unit);
      const has = f.selection.some((u) => unitKey(u) === key);
      return {
        ...f,
        selection: has ? f.selection.filter((u) => unitKey(u) !== key) : [...f.selection, unit],
      };
    });

  const toggleAllVisible = () =>
    setForm((f) => {
      if (allVisibleSelected) {
        const visible = new Set(selectableVisible.map(unitKey));
        return { ...f, selection: f.selection.filter((u) => !visible.has(unitKey(u))) };
      }
      const map = new Map(f.selection.map((u) => [unitKey(u), u]));
      selectableVisible.forEach((u) => map.set(unitKey(u), u));
      return { ...f, selection: [...map.values()] };
    });

  const clearSelection = () => setForm((f) => ({ ...f, selection: [] }));

  const payloadBase = () => {
    const idsOf = (kind) => form.selection.filter((u) => u.kind === kind).map((u) => u.value);
    const holdingIds = idsOf(UNIT_HOLDING);
    return {
      companyIds: idsOf(UNIT_COMPANY),
      // Solo si hay alguno: una tanda de comercios sale igual que antes.
      ...(holdingIds.length ? { holdingIds } : {}),
      chargeMode: form.chargeMode,
      periodStart: resolvedRange.start,
      periodEnd: resolvedRange.end,
      currency,
      // El IVA (modo + tasa) lo resuelve el backend desde el contrato y el país del
      // operador; el front ya no fuerza taxRate.
      unpricedFallbackPrice:
        form.unpricedFallbackPrice !== '' && form.unpricedFallbackPrice != null
          ? Number(form.unpricedFallbackPrice)
          : undefined,
      adjustments: form.adjustments.length ? form.adjustments : undefined,
    };
  };

  // Texto de un error de preview/generate: 404 holding_not_found y 503 holdings_unavailable
  // afectan a la tanda entera (también a una de solo comercios) y tienen su explicación; el
  // resto es el mensaje de billing, como antes.
  const batchErrorText = (err, fallback) =>
    holdingCodeText(err, t) ?? apiErrorMessage(err, t(fallback));

  // Nombres de holdings para los mensajes (los que no se conocen, por id).
  const holdingNamesText = (ids) =>
    ids
      .map((id) => holdings.find((h) => String(h.id) === String(id))?.name ?? id)
      .join(', ');

  // Un billing anterior a los holdings: ignora `holdingIds` y no devuelve `perHolding`, o
  // responde 400 "companyIds should not be empty" a una tanda de solo holdings. Se corta con un
  // error explícito en vez de seguir como si no se hubieran pedido.
  const previewHoldingsProblem = (payload, data) => {
    const missing = holdingsMissingFromPreview(payload.holdingIds, data);
    if (!missing.length) return null;
    return t('El servicio de cobro no devolvió la vista previa de {{n}} holding(s) ({{names}}): puede que todavía no soporte holdings. No sigas con esta tanda y avisá a soporte.', {
      n: missing.length,
      names: holdingNamesText(missing),
    });
  };
  const holdingsOnlyRejected = (payload, err) =>
    (payload.holdingIds?.length ?? 0) > 0 &&
    payload.companyIds.length === 0 &&
    apiErrorStatus(err) === 400 &&
    /companyIds/.test(String(apiErrorMessage(err) ?? ''));
  const previewErrorText = (payload, err, fallback) =>
    holdingsOnlyRejected(payload, err)
      ? t('El servicio de cobro no devolvió la vista previa de {{n}} holding(s) ({{names}}): puede que todavía no soporte holdings. No sigas con esta tanda y avisá a soporte.', {
          n: payload.holdingIds.length,
          names: holdingNamesText(payload.holdingIds),
        })
      : batchErrorText(err, fallback);

  // Recalcula el paso 3. Si la respuesta no trae los holdings pedidos, descarta la vista previa
  // y vuelve al paso 2 (devuelve false): no se puede confirmar algo que billing no calculó. Un
  // error de red deja la vista previa anterior, como siempre.
  const recalc = async (overrides = {}) => {
    const payload = { ...payloadBase(), ...overrides };
    try {
      const data = await runPreview(payload);
      const problem = previewHoldingsProblem(payload, data);
      if (problem) {
        clearPreview();
        setStep(2);
        toast.error(problem);
        return false;
      }
      setPreviewStale(false);
    } catch (err) {
      toast.error(previewErrorText(payload, err, 'No se pudo recalcular'));
    }
    return true;
  };

  // Rótulo de la unidad de un ajuste: el holding si lleva `holdingId`, si no el comercio.
  const adjustmentUnitLabel = (a) => {
    const key = a.holdingId
      ? unitKey({ kind: UNIT_HOLDING, value: a.holdingId })
      : unitKey({ kind: UNIT_COMPANY, value: a.companyId });
    return form.selection.find((u) => unitKey(u) === key)?.label ?? a.holdingId ?? a.companyId;
  };

  const addAdjustment = async () => {
    const amount = Number(adjForm.amount);
    const unit = form.selection.find((u) => unitKey(u) === adjForm.unit);
    if (!unit) {
      return toast.error(
        selectedHoldingCount > 0
          ? t('Elige un comercio o un holding para el ajuste')
          : t('Elige un comercio para el ajuste'),
      );
    }
    if (!amount || amount <= 0) return toast.error(t('El monto del ajuste debe ser mayor a 0'));
    // Un ajuste con `holdingId` va a la nota consolidada del holding.
    const item = {
      ...(unit.kind === UNIT_HOLDING ? { holdingId: unit.value } : { companyId: unit.value }),
      type: adjForm.type,
      label: adjForm.label?.trim() || (adjForm.type === 'surcharge' ? t('Recargo') : t('Descuento')),
      amount: Math.round(amount),
    };
    const newList = [...form.adjustments, item];
    setForm((f) => ({ ...f, adjustments: newList }));
    setAdjForm((a) => ({ ...a, label: '', amount: '' }));
    await recalc({ adjustments: newList });
  };

  const removeAdjustment = async (idx) => {
    const newList = form.adjustments.filter((_, i) => i !== idx);
    setForm((f) => ({ ...f, adjustments: newList }));
    await recalc({ adjustments: newList.length ? newList : undefined });
  };

  // Holdings de la vista previa (nota consolidada) y todas las unidades de la tanda. Los
  // avisos de la tanda (sin tarifar, modos, devueltos, sobredimensionados) suman comercios y
  // holdings; el ciclo y el tramo de cada holding se explican en su propio bloque.
  const perHolding = useMemo(() => preview?.perHolding ?? [], [preview]);
  const previewUnits = useMemo(
    () => [...(preview?.perCompany ?? []), ...(preview?.perHolding ?? [])],
    [preview],
  );

  // Comercios de la vista previa con tarifa escalonada: los que quedaron bloqueados por no
  // cubrir su ciclo, y los que sí resolvieron tramo.
  const cycleBlocked = useMemo(
    () => (preview?.perCompany ?? []).filter((c) => c.volumeScale?.cycleMismatch),
    [preview],
  );
  const tieredCompanies = useMemo(
    () => (preview?.perCompany ?? []).filter((c) => c.volumeScale?.tier),
    [preview],
  );
  // Comercios cuya nota superaría el máximo de líneas por nota (MAX_NOTE_LINE_ITEMS de
  // billing: la nota es UN documento, con tope de tamaño). Billing los deja en cero y generate
  // los omite con `too_many_lines`; el remedio es emitir por un período más corto.
  const tooManyLinesCompanies = useMemo(
    () => (preview?.perCompany ?? []).filter((c) => c.tooManyLines === true),
    [preview],
  );

  const totalUnpriced = useMemo(
    () => previewUnits.reduce((s, c) => s + (c.unpricedCount || 0), 0),
    [previewUnits],
  );

  // Avisos de modo de la tanda, que billing calcula por comercio (`modeWarnings`):
  //   · billedInOtherMode: paquetes del período que quedan FUERA porque ya se cobraron en el
  //     otro modo (billing no deja cobrar dos veces el mismo paquete);
  //   · unbilledBeforePeriod: el "hueco", paquetes sin cobrar en ningún modo cuya fecha del
  //     modo elegido cae antes del período. Ninguna nota de este período los va a incluir.
  // Son informativos: no cambian el total ni bloquean. `?? 0` cubre a un billing anterior a
  // estos avisos, que no manda el campo.
  const modeWarningTotals = useMemo(() => {
    const rows = previewUnits;
    return {
      billedInOtherMode: rows.reduce((s, c) => s + (c.modeWarnings?.billedInOtherMode ?? 0), 0),
      unbilledBeforePeriod: rows.reduce(
        (s, c) => s + (c.modeWarnings?.unbilledBeforePeriod ?? 0),
        0,
      ),
    };
  }, [previewUnits]);
  const chargingCollected = form.chargeMode === 'recolectado';

  // Devoluciones de toda la tanda: cuántas hay y cuánto se descuenta. Se recalcula desde
  // comercios y holdings como fallback por si la respuesta no trae los agregados `grand*`.
  const returnedTotals = useMemo(() => {
    const rows = previewUnits;
    return {
      count:
        preview?.grandReturnedCount ??
        rows.reduce((s, c) => s + (c.returned?.packagesCount || 0), 0),
      amount:
        preview?.grandReturnedAmount ??
        rows.reduce((s, c) => s + (c.returned?.amount || 0), 0),
      unpriced: rows.reduce((s, c) => s + (c.returned?.unpricedCount || 0), 0),
    };
  }, [preview, previewUnits]);

  // Sobredimensionados que se están cobrando al precio normal porque su categoría no
  // tiene recargo configurado. No bloquean: el paquete se cobra igual, pero es plata que
  // el operador cree estar cobrando. Se agrupa por categoría para que sepa QUÉ configurar.
  const oversizedGap = useMemo(() => {
    const rows = previewUnits;
    const byCategory = new Map();
    for (const c of rows) {
      for (const g of c.oversizedWithoutSurcharge?.byCategory ?? []) {
        byCategory.set(g.category, (byCategory.get(g.category) ?? 0) + g.count);
      }
    }
    return {
      count:
        preview?.grandOversizedWithoutSurcharge ??
        rows.reduce((s, c) => s + (c.oversizedWithoutSurcharge?.count || 0), 0),
      byCategory: [...byCategory.entries()]
        .map(([category, count]) => ({ category, count }))
        .sort((a, b) => b.count - a.count),
    };
  }, [preview, previewUnits]);

  // Sobredimensionados de toda la tanda y lo que aportan al total. Es el HECHO, hermano
  // del contador de devueltos: sin esto el paso 3 no explica de dónde sale el número
  // —los devueltos restan a la vista y los sobredimensionados suman sin mostrarse—.
  const oversizedTotals = useMemo(() => {
    const rows = previewUnits;
    // Desglose por categoría de toda la tanda: un "6 sobredimensionados" no dice si el
    // recargo viene de seis XL o de un 3XL, que es lo que el operador necesita revisar.
    const byCategory = new Map();
    for (const c of rows) {
      for (const g of c.oversized?.byCategory ?? []) {
        const acc = byCategory.get(g.category) ?? {
          category: g.category,
          count: 0,
          surchargeAmount: 0,
        };
        acc.count += g.count || 0;
        acc.surchargeAmount += g.surchargeAmount || 0;
        byCategory.set(g.category, acc);
      }
    }
    return {
      count:
        preview?.grandOversizedCount ??
        rows.reduce((s, c) => s + (c.oversized?.count || 0), 0),
      amount:
        preview?.grandOversizedSurcharge ??
        rows.reduce((s, c) => s + (c.oversized?.surchargeAmount || 0), 0),
      byCategory: [...byCategory.values()].sort(bySizeCategory),
    };
  }, [preview, previewUnits]);

  // Comercios de la tanda que billing NO tarifa solos: los cobra su holding
  // (`belongs_to_holding`) o están asociados a más de un holding (`multiple_holdings`).
  const absorbedSkips = useMemo(
    () => (preview?.skipped ?? []).filter((s) => s.reason === 'belongs_to_holding'),
    [preview],
  );
  const conflictSkips = useMemo(
    () => (preview?.skipped ?? []).filter((s) => s.reason === 'multiple_holdings'),
    [preview],
  );
  const blockedHoldings = useMemo(() => perHolding.filter((h) => h.blocked), [perHolding]);
  const tooManyLinesHoldings = useMemo(
    () => blockedHoldings.filter(blockedByLineLimit),
    [blockedHoldings],
  );
  // Holdings pedidos que billing deja fuera de la vista previa (p. ej. `consolidated_disabled`:
  // alguien apagó su cobro consolidado después de que se cargó el asistente). No se emiten.
  const skippedHoldingsInPreview = useMemo(
    () => (preview?.skipped ?? []).filter((s) => s.holdingId),
    [preview],
  );
  // Notas que se van a emitir: una por comercio que no supera el máximo de líneas y una por
  // holding sin bloqueo.
  const noteCount =
    (preview?.companyCount ?? 0) -
    tooManyLinesCompanies.length +
    perHolding.length -
    blockedHoldings.length;

  const clientName = (companyId) =>
    catalog.find((c) => c.value === String(companyId))?.label ?? null;
  const holdingName = (holdingId) =>
    holdings.find((h) => String(h.id) === String(holdingId))?.name ??
    perHolding.find((h) => h.holdingId === String(holdingId))?.name ??
    null;
  // Los bloqueados por el máximo de líneas se nombran aparte, con su remedio.
  const notEmittedHoldingNames = [
    ...blockedHoldings.filter((h) => !blockedByLineLimit(h)).map((h) => h.name || h.holdingId),
    ...skippedHoldingsInPreview.map((s) => holdingName(s.holdingId) ?? s.holdingId),
  ];
  // Comercios y holdings cuya nota superaría el máximo de líneas: no se emiten.
  const tooManyLinesNames = [
    ...tooManyLinesCompanies.map((c) => clientName(c.companyId) ?? c.companyId),
    ...tooManyLinesHoldings.map((h) => h.name || holdingName(h.holdingId) || h.holdingId),
  ];
  // Nombre de una empresa de un holding: del catálogo de comercios o de la membresía que
  // informa contracts (las empresas sin contrato activo no están en el catálogo).
  const memberName = (companyId) => {
    const id = String(companyId);
    for (const h of holdings) {
      const member = (h.companies ?? []).find((c) => String(c.id) === id);
      if (member?.name) return member.name;
    }
    return clientName(id);
  };

  // Renderiza "XL: 5 (+$15.000) · 2XL: 1 (+$3.200)". Una categoría sin recargo
  // configurado se marca aparte: cuenta paquetes pero no aporta plata.
  const formatOversizedCategories = (byCategory, cur) =>
    [...(byCategory ?? [])]
      .sort(bySizeCategory)
      .map((g) =>
        g.surchargeAmount > 0
          ? t('{{cat}}: {{n}} (+{{amount}})', {
              cat: g.category,
              n: g.count,
              amount: formatCurrency(g.surchargeAmount, cur),
            })
          : t('{{cat}}: {{n}} (sin recargo)', { cat: g.category, n: g.count }),
      )
      .join(' · ');

  // Paso 1 → 2: validar rango.
  const goCompanies = () => {
    if (!resolvedRange.start || !resolvedRange.end) {
      return toast.error(t('Selecciona un rango de fechas válido'));
    }
    setStep(2);
  };

  // Paso 2 → 3: validar la selección y calcular preview.
  const goPreview = async () => {
    if (form.selection.length === 0) return toast.error(t('Selecciona al menos un comercio'));
    const payload = payloadBase();
    try {
      const data = await runPreview(payload);
      const problem = previewHoldingsProblem(payload, data);
      if (problem) {
        clearPreview();
        toast.error(problem);
        return;
      }
      setPreviewStale(false);
      setStep(3);
    } catch (err) {
      toast.error(previewErrorText(payload, err, 'No se pudo calcular la vista previa'));
    }
  };

  // Datos fiscales guardados en el paso "Datos del holding": se toman de la respuesta del
  // PATCH (sin releer la lista) para que el formulario muestre lo guardado. La vista previa se
  // recalcula cuando el operador lo pide (ver HoldingPreviewCard); hasta entonces queda vieja.
  const handleHoldingFiscalSaved = (view) => {
    setPreviewStale(true);
    if (!view?.id) return;
    setHoldings((list) =>
      list.map((h) => (String(h.id) === String(view.id) ? { ...h, ...view } : h)),
    );
  };
  const handleHoldingProfileSaved = () => setPreviewStale(true);

  const handleGenerate = async () => {
    try {
      // Versiones del rate card que se previsualizaron y se están confirmando, por
      // comercio. El backend omite cualquiera cuya versión vigente haya cambiado
      // (el operador editó las tarifas entre el preview y esta confirmación).
      const expectedPricingVersions = Object.fromEntries(
        (preview?.perCompany ?? []).map((c) => [c.companyId, c.pricingVersion]),
      );
      // Cuarto eje de anti-carrera: el CONTRATO con que se tarifó cada comercio en la vista
      // previa (null = sin contrato). Dos contratos pueden tener la misma versión de tarifa;
      // si al emitir billing resuelve otro, omite el comercio con `pricing_changed`. Solo con
      // un billing que informa `contractId` (uno anterior no lo manda y no se inventa).
      const previewedContracts = (preview?.perCompany ?? []).filter(
        (c) => c.contractId !== undefined,
      );
      // Comercios sin ningún contrato que el operador eligió a mano y vio en la vista previa
      // como "sin contrato · tarifa base": la confirmación explícita que billing exige para
      // emitirles nota (sin ella los omite con `no_contract_unconfirmed`). Solo cuentan los
      // que billable-companies ya marcó 'none' en el paso 2, el grupo aparte que no entra en
      // "Seleccionar todos". Uno que llegó por "Seleccionar todos" desde la lista de contratos
      // activos (p. ej. su contrato empieza después del período) no se elige uno a uno: sin
      // billable-companies (falló o no cargó) no se confirma ninguno y billing los omite.
      const selectedCompanyIds = new Set(
        form.selection.filter((u) => u.kind === UNIT_COMPANY).map((u) => String(u.value)),
      );
      const confirmedUncontractedIds = billableReady
        ? (preview?.perCompany ?? [])
            .filter(
              (c) =>
                c.contractSource === 'none' &&
                selectedCompanyIds.has(String(c.companyId)) &&
                billableById.get(String(c.companyId))?.contractSource === 'none',
            )
            .map((c) => c.companyId)
        : [];
      // Solo se emiten los holdings que la vista previa mostró emitibles. Billing vuelve a
      // evaluar el bloqueo al emitir: si entretanto se completaron sus datos (en la ficha, en
      // otra pestaña u otra sesión) emitiría y enviaría por correo una nota que el operador vio
      // bloqueada y cuyo total no está en el que confirmó. Sus ajustes tampoco viajan.
      const emittableHoldings = perHolding.filter((h) => !h.blocked);
      const emittableIds = new Set(emittableHoldings.map((h) => String(h.holdingId)));
      const { holdingIds: selectedHoldingIds, adjustments, ...base } = payloadBase();
      const holdingIds = (selectedHoldingIds ?? []).filter((id) => emittableIds.has(String(id)));
      const emittableAdjustments = (adjustments ?? []).filter(
        (a) => !a.holdingId || emittableIds.has(String(a.holdingId)),
      );
      const res = await generate({
        ...base,
        ...(holdingIds.length ? { holdingIds } : {}),
        adjustments: emittableAdjustments.length ? emittableAdjustments : undefined,
        dateRangePreset: form.preset,
        notes: form.notes || undefined,
        expectedPricingVersions,
        ...(previewedContracts.length
          ? {
              expectedContractIds: Object.fromEntries(
                previewedContracts.map((c) => [c.companyId, c.contractId ?? null]),
              ),
            }
          : {}),
        ...(confirmedUncontractedIds.length ? { confirmedUncontractedIds } : {}),
        // Hermano global del anterior: la tarifa base del operador (Configuración → Cobro
        // a comercios) alimenta el precio de TODOS estos comercios, y su versión no se
        // mueve cuando cambia la del contrato. Sin mandarla, editar esa tarifa entre este
        // paso y el anterior emitiría notas con un total distinto al confirmado.
        expectedPricingConfigVersion: preview?.pricingConfigVersion,
        // Tercer eje de anti-carrera: el TRAMO por volumen que se previsualizó. Ni la
        // versión del contrato ni la de la config del operador se mueven porque entre un
        // paquete nuevo o se anule otra nota del mismo ciclo, y cualquiera de las dos cosas
        // puede cambiar el tramo y con él el total. `null` = no se aplicó ninguno.
        expectedVolumeTiers: Object.fromEntries(
          (preview?.perCompany ?? []).map((c) => [
            c.companyId,
            c.volumeScale?.tier?.minQty ?? null,
          ]),
        ),
        // Los mismos dos guards para cada holding, en mapas APARTE (su tarifa y su tramo no
        // son los de ningún comercio). Solo viajan si se emite algún holding.
        ...(holdingIds.length
          ? {
              expectedHoldingPricingVersions: Object.fromEntries(
                emittableHoldings.map((h) => [h.holdingId, h.pricingVersion]),
              ),
              expectedHoldingVolumeTiers: Object.fromEntries(
                emittableHoldings.map((h) => [h.holdingId, h.volumeScale?.tier?.minQty ?? null]),
              ),
            }
          : {}),
      });

      // Cada holding enviado tiene que volver emitido (`created`) u omitido (`skipped`). Si no
      // (billing sin holdings, o revertido después de la vista previa), no se emitió nada para
      // él: se informa lo que sí se generó y se vuelve al paso 2, sin cerrar el asistente.
      const unanswered = unansweredHoldingIds(holdingIds, res.created, res.skipped);
      if (unanswered.length) {
        if (res.createdCount > 0) {
          toast.success(t('Se generaron {{n}} nota(s) de cobro', { n: res.createdCount }));
          onGenerated?.(res);
        }
        toast.error(
          t('El servicio de cobro no informó la nota de {{n}} holding(s) ({{names}}): no se emitió. Puede que todavía no soporte holdings; avisá a soporte antes de volver a intentarlo.', {
            n: unanswered.length,
            names: holdingNamesText(unanswered),
          }),
        );
        clearPreview();
        setStep(2);
        return;
      }

      // Unidades omitidas porque su tarifa cambió desde la vista previa: no se cierra el
      // wizard, se recalcula y se vuelve al paso de preview para reconfirmar. `volume_changed`
      // va en el mismo grupo: mismo remedio (recalcular y reconfirmar) y misma causa desde el
      // punto de vista del operador — el total que aprobó ya no vale. Comercios y holdings se
      // informan por separado: un holding no es "un comercio".
      const skipped = res.skipped ?? [];
      const isChange = (s) => s.reason === 'pricing_changed' || s.reason === 'volume_changed';
      const changed = skipped.filter(isChange);
      const changedCompanies = changed.filter((s) => !s.holdingId);
      const changedHoldings = changed.filter((s) => s.holdingId);
      // Un holding omitido por cualquier otro motivo es plata que el operador cree cobrada: se
      // nombra con su motivo (datos, tarifa, membresía, ciclo, sin paquetes, emitido en
      // paralelo), también cuando además hubo cambios de tarifa. Los problemas de la nota de la
      // unidad van aparte (warnUnitProblems).
      const otherSkippedHoldings = skipped.filter(
        (s) => s.holdingId && !isChange(s) && !UNIT_PROBLEM_REASONS.has(s.reason),
      );
      const holdingWithReason = (s) =>
        t('{{name}} ({{reason}})', {
          name: holdingName(s.holdingId) ?? s.holdingId,
          reason: holdingSkipReasonText(s.reason, t),
        });
      const warnOtherSkippedHoldings = () => {
        if (!otherSkippedHoldings.length) return;
        toast.warn(
          t('No se emitió la nota de {{n}} holding(s): {{names}}.', {
            n: otherSkippedHoldings.length,
            names: otherSkippedHoldings.map(holdingWithReason).join(', '),
          }),
        );
      };
      // Comercios y holdings (por nombre) cuya nota no se emitió por un problema de la propia
      // nota, cada motivo con su remedio:
      //   · too_many_lines: llega aunque la vista previa no lo mostrara (billing vuelve a
      //     tarifar al emitir); se parte el período;
      //   · emission_failed: la emisión de ESA unidad falló y la tanda siguió. Si falló antes de
      //     guardar la nota, billing liberó sus paquetes y se puede reintentar; si falló después
      //     (p. ej. al compensar el pago contra entrega o al guardar el PDF), la nota existe y
      //     está emitida aunque no venga en `created`. Por eso se pide mirar la lista antes.
      const skippedUnitNames = (list) =>
        list
          .map((s) =>
            s.holdingId
              ? holdingName(s.holdingId) ?? s.holdingId
              : clientName(s.companyId) ?? s.companyId,
          )
          .join(', ');
      const lineLimitSkips = skipped.filter((s) => s.reason === 'too_many_lines');
      const failedSkips = skipped.filter((s) => s.reason === 'emission_failed');
      const warnUnitProblems = () => {
        if (lineLimitSkips.length) {
          toast.warn(
            t('No se emitieron {{n}} nota(s) porque superan el máximo de líneas por nota ({{names}}): emití por un período más corto.', {
              n: lineLimitSkips.length,
              names: skippedUnitNames(lineLimitSkips),
            }),
          );
        }
        if (failedSkips.length) {
          toast.error(
            t('No se pudieron emitir {{n}} nota(s) ({{names}}): revisá la lista de notas antes de reintentar; si la nota aparece emitida no la vuelvas a generar.', {
              n: failedSkips.length,
              names: skippedUnitNames(failedSkips),
            }),
          );
        }
      };

      if (changed.length) {
        if (res.createdCount > 0) {
          toast.success(t('Se generaron {{n}} nota(s) de cobro', { n: res.createdCount }));
          onGenerated?.(res);
        }
        if (changedCompanies.length) {
          toast.warn(
            changedCompanies.some((s) => s.reason === 'volume_changed')
              ? t('El volumen o las tarifas de {{n}} comercio(s) cambiaron desde la vista previa; se omitieron. Revisá los montos y confirmá de nuevo.', { n: changedCompanies.length })
              : t('Las tarifas de {{n}} comercio(s) cambiaron desde la vista previa; se omitieron. Revisá los montos y confirmá de nuevo.', { n: changedCompanies.length }),
          );
        }
        if (changedHoldings.length) {
          toast.warn(
            t('La tarifa o el volumen de {{n}} holding(s) cambiaron desde la vista previa: {{names}}. No se emitieron: revisá los montos y confirmá de nuevo.', {
              n: changedHoldings.length,
              names: changedHoldings.map((s) => holdingName(s.holdingId) ?? s.holdingId).join(', '),
            }),
          );
        }
        warnOtherSkippedHoldings();
        warnUnitProblems();
        if (await recalc()) setStep(3);
        return;
      }

      toast.success(
        t('Se generaron {{n}} nota(s) de cobro', { n: res.createdCount }) +
          (skipped.length ? ` (${t('{{n}} omitidas', { n: skipped.length })})` : ''),
      );
      warnOtherSkippedHoldings();
      warnUnitProblems();
      // Comercios omitidos por otros motivos (contrato, ciclo, sin entregas…). Los holdings y
      // los problemas de la nota ya se avisaron arriba; acá el wizard se cierra y, sin este
      // detalle, esos comercios quedarían sin nota y sin que el operador sepa por qué.
      const otherSkippedCompanies = skipped.filter(
        (s) => !s.holdingId && !UNIT_PROBLEM_REASONS.has(s.reason),
      );
      if (otherSkippedCompanies.length) {
        const detalle = summarizeSkipped(otherSkippedCompanies, t, (id) => clientName(id) ?? id).join(' · ');
        if (detalle) toast.warn(t('Comercios sin nota de cobro: {{detalle}}', { detalle }));
      }
      close();
      onGenerated?.(res);
    } catch (err) {
      toast.error(batchErrorText(err, 'Error al generar las notas de cobro'));
    }
  };

  // Fila del paso 2: casilla + nombre + aviso del contrato con que se tarifaría (vencido,
  // inactivo o ninguno, según billable-companies).
  const renderUnitRow = (u) => {
    const key = unitKey(u);
    const checked = selectedKeys.has(key);
    const secondary = commerceSecondaryLine(u);
    const Icon = u.kind === UNIT_HOLDING ? Network : Building2;
    const contractBadge = listContractBadge(contractInfoOf(u));
    return (
      <label
        key={key}
        className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
          checked ? 'bg-brand-50' : 'hover:bg-bg-muted'
        }`}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={() => toggleUnit(u)}
          className="w-4 h-4 accent-brand-500 shrink-0"
        />
        <Icon className={`w-4 h-4 shrink-0 ${checked ? 'text-brand-600' : 'text-fg-muted'}`} />
        <span className="flex flex-col min-w-0">
          <span className="flex items-center gap-2 min-w-0">
            <span className={`text-sm truncate ${checked ? 'text-brand-700 font-medium' : 'text-fg'}`}>
              {u.label}
            </span>
            {u.kind === UNIT_HOLDING && (
              <span className="bill-badge bill-badge--info bill-badge--sm shrink-0">
                {t('Holding · {{n}} empresas', { n: u.memberCount })}
              </span>
            )}
            {contractBadge && (
              <span className={`bill-badge bill-badge--${contractBadge.tone} bill-badge--sm shrink-0`}>
                {t(contractBadge.label)}
              </span>
            )}
          </span>
          {secondary && (
            <span className="text-xs text-fg-muted truncate">{secondary}</span>
          )}
        </span>
      </label>
    );
  };

  const footer = (() => {
    if (step === 1) {
      return (
        <>
          <Button variant="outline" onClick={close}>{t('Cancelar')}</Button>
          <Button onClick={goCompanies} iconRight={<ArrowRight className="w-4 h-4" />}>
            {t('Siguiente')}
          </Button>
        </>
      );
    }
    if (step === 2) {
      return (
        <>
          <Button variant="outline" onClick={() => setStep(1)} iconLeft={<ArrowLeft className="w-4 h-4" />}>{t('Atrás')}</Button>
          <Button
            onClick={goPreview}
            loading={previewing}
            // Mientras llegan los comercios del período la selección todavía se puede podar.
            disabled={form.selection.length === 0 || companiesLoading}
            iconRight={<ArrowRight className="w-4 h-4" />}
          >
            {t('Ver vista previa')}
          </Button>
        </>
      );
    }
    if (step === 3) {
      return (
        <>
          <Button variant="outline" onClick={() => setStep(2)} iconLeft={<ArrowLeft className="w-4 h-4" />}>{t('Atrás')}</Button>
          <Button
            disabled={previewing || previewStale || !preview || preview.grandTotal <= 0}
            onClick={() => setStep(4)}
            iconRight={<ArrowRight className="w-4 h-4" />}
          >
            {t('Confirmar')}
          </Button>
        </>
      );
    }
    return (
      <>
        <Button variant="outline" onClick={() => setStep(3)} iconLeft={<ArrowLeft className="w-4 h-4" />}>{t('Atrás')}</Button>
        <Button onClick={handleGenerate} loading={saving}>{t('Generar notas de cobro')}</Button>
      </>
    );
  })();

  return (
    <Modal open={isOpen} onClose={close} size="lg" title={t('Cobrar a comercios')} footer={footer}>
      {/* Stepper */}
      <ol className="bill-stepper" aria-label={t('Pasos')} style={{ marginTop: 0, marginBottom: '1.25rem' }}>
        {STEPS.map((label, i) => {
          const idx = i + 1;
          const state = idx < step ? 'done' : idx === step ? 'current' : 'todo';
          return (
            <li
              key={label}
              className={`bill-stepper__step bill-stepper__step--${state}`}
              aria-current={state === 'current' ? 'step' : undefined}
            >
              <span className="bill-stepper__dot">{state === 'done' ? '✓' : idx}</span>
              <span className="bill-stepper__label">{t(label)}</span>
            </li>
          );
        })}
      </ol>

      {/* ── Paso 1: periodo + modo ── */}
      {step === 1 && (
        <>
          <div className="bill-field" style={{ marginBottom: '1rem' }}>
            <label className="bill-label">{t('Periodo a cobrar')}</label>
            <Select name="preset" value={form.preset} onChange={handleChange}>
              {DATE_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>{t(p.label)}</option>
              ))}
            </Select>
          </div>

          {form.preset === 'custom' && (
            <div className="bill-form-row" style={{ marginBottom: '1rem' }}>
              <div className="bill-field">
                <label className="bill-label">{t('Desde')}</label>
                <Input type="date" name="periodStart" value={form.periodStart || ''} max={form.periodEnd || todayYMD()} onChange={handleChange} />
              </div>
              <div className="bill-field">
                <label className="bill-label">{t('Hasta')}</label>
                <Input type="date" name="periodEnd" value={form.periodEnd || ''} min={form.periodStart || ''} onChange={handleChange} />
              </div>
            </div>
          )}

          <div className="bill-field" style={{ marginBottom: '1rem' }}>
            <label className="bill-label">{t('Modo de cobro')}</label>
            <div className="bill-radio-group">
              {CHARGE_MODES.map((opt) => {
                const Icon = MODE_ICON[opt.value] ?? Truck;
                return (
                  <label
                    key={opt.value}
                    className={`bill-radio${form.chargeMode === opt.value ? ' bill-radio--active' : ''}`}
                  >
                    <input
                      type="radio"
                      name="chargeMode"
                      value={opt.value}
                      checked={form.chargeMode === opt.value}
                      onChange={handleChange}
                    />
                    <Icon className="w-[18px] h-[18px]" aria-hidden />
                    {t(opt.label)}
                  </label>
                );
              })}
            </div>
          </div>

          <div className="bill-recap">
            <div className="bill-recap__grid">
              <span className="bill-recap__k">{t('Rango')}:</span>
              <span className="bill-recap__v">{resolvedRange.start || '—'} → {resolvedRange.end || '—'}</span>
              <span className="bill-recap__k">{t('Modo')}:</span>
              <span className="bill-recap__v">{t(CHARGE_MODE_LABELS[form.chargeMode])}</span>
            </div>
          </div>
        </>
      )}

      {/* ── Paso 2: comercios (selector cómodo con buscador + checkboxes) ── */}
      {step === 2 && (
        <>
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <label className="bill-label" style={{ margin: 0 }}>
              {t('Comercios a cobrar')} <span className="text-status-error">*</span>
            </label>
            <span className="bill-badge bill-badge--info">
              {t('{{n}} seleccionados', { n: form.selection.length })}
            </span>
          </div>

          {/* Sin los holdings no se sabe qué comercios cobra su holding: aparecen sueltos y
              billing los omite (la vista previa lo informa). Se avisa, no se bloquea. */}
          {/* No bloquea: mientras tanto se puede elegir comercios, y los que resulten de un
              holding se sacan solos de la selección al llegar. */}
          {holdingsStatus === 'loading' && (
            <p className="bill-hint" role="status" style={{ marginBottom: '0.5rem' }}>
              {t('Cargando los holdings del operador…')}
            </p>
          )}
          {holdingsStatus === 'error' && (
            <div
              role="alert"
              className="flex items-start gap-2 mb-2 rounded-md border border-border bg-status-warning-soft p-2.5 text-xs text-status-warning"
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" aria-hidden />
              <span className="flex-1">
                {holdingsError}{' '}
                {t('Los holdings no se pueden cobrar ahora, y las empresas de un holding con cobro consolidado que elijas se omitirán al cobrar.')}
              </span>
              <button
                type="button"
                onClick={loadHoldings}
                className="shrink-0 font-medium underline hover:no-underline"
              >
                {t('Reintentar')}
              </button>
            </div>
          )}

          {/* Sin los comercios con pedidos por cobrar se ofrecen solo los de contrato activo:
              se avisa (no bloquea) para que un comercio que falta no parezca sin pedidos. Si
              billing rechazó el período, reintentar falla igual: se pide acortarlo. */}
          {billableFailed && (
            <div
              role="status"
              className="flex items-start gap-2 mb-2 rounded-md border border-border bg-status-warning-soft p-2.5 text-xs text-status-warning"
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" aria-hidden />
              <span className="flex-1">
                {billablePeriodRejected
                  ? t('Para incluir comercios sin contrato vigente, elige un período de hasta 62 días.')
                  : t('No se pudieron consultar los comercios con pedidos por cobrar; se muestran solo los con contrato activo.')}
              </span>
              {!billablePeriodRejected && (
                <button
                  type="button"
                  onClick={() => setBillableRetry((n) => n + 1)}
                  className="shrink-0 font-medium underline hover:no-underline"
                >
                  {t('Reintentar')}
                </button>
              )}
            </div>
          )}

          {/* Empresas que no se ofrecen sueltas porque las cobra su holding encendido, con
              cuáles son: sin la lista, una empresa que "falta" parece un error de datos. */}
          {absorbedCount > 0 && (
            <div className="flex items-start gap-2 mb-2 rounded-md border border-border bg-bg-subtle p-2.5 text-xs text-fg-muted">
              <Network className="w-4 h-4 shrink-0 mt-px" aria-hidden />
              <div className="min-w-0">
                <span>
                  {t('{{n}} comercio(s) no aparecen sueltos: se cobran dentro de su holding, que tiene el cobro consolidado encendido. Elegí el holding para cobrarlos.', {
                    n: absorbedCount,
                  })}
                </span>
                {absorbedGroups.length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer font-medium text-fg">{t('Ver cuáles')}</summary>
                    <ul className="mt-1 space-y-0.5" aria-label={t('Empresas que se cobran dentro de un holding')}>
                      {absorbedGroups.map((group) => (
                        <li key={group.id}>
                          {t('{{holding}}: {{companies}}', {
                            holding: group.name,
                            companies: group.members.join(', '),
                          })}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </div>
          )}
          {disabledHoldingCount > 0 && (
            <div className="flex items-start gap-2 mb-2 rounded-md border border-border bg-bg-subtle p-2.5 text-xs text-fg-muted">
              <Info className="w-4 h-4 shrink-0 mt-px" aria-hidden />
              <span>
                {t('{{n}} holding(s) tienen el cobro consolidado apagado y no se ofrecen: sus empresas aparecen como comercios sueltos y se cobran con su propio contrato.', {
                  n: disabledHoldingCount,
                })}
              </span>
            </div>
          )}

          {/* Comercios ocultos por su ciclo de facturación. Sin este aviso, un comercio que
              "desapareció" de la lista parece un bug de datos y no una consecuencia del
              período elegido en el paso anterior. */}
          {hiddenByCycle > 0 && (
            <div className="flex items-start gap-2 mb-2 rounded-md border border-border bg-bg-subtle p-2.5 text-xs text-fg-muted">
              <Info className="w-4 h-4 shrink-0 mt-px" />
              <span>
                {cycle === null
                  ? t(
                      'No se muestran {{n}} comercios con tarifa escalonada: su nota tiene que cubrir una semana (dom–sáb) o un mes calendario completo, y el período elegido no es ninguno de los dos.',
                      { n: hiddenByCycle },
                    )
                  : cycle === 'weekly'
                    ? t(
                        'No se muestran {{n}} comercios: se facturan por mes y el período elegido es una semana.',
                        { n: hiddenByCycle },
                      )
                    : t(
                        'No se muestran {{n}} comercios: se facturan por semana y el período elegido es un mes.',
                        { n: hiddenByCycle },
                      )}{' '}
                {t('Cambiá el período en el paso anterior para verlos.')}
              </span>
            </div>
          )}

          {companiesLoading ? (
            <Skeleton className="h-64 w-full rounded-lg" />
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              {/* Buscador */}
              <div className="relative border-b border-border">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted pointer-events-none" />
                <input
                  type="text"
                  value={companyQuery}
                  onChange={(e) => setCompanyQuery(e.target.value)}
                  placeholder={t('Buscar por nombre, razón social o RUT…')}
                  className="w-full h-11 bg-bg text-fg pl-9 pr-3 text-sm focus:outline-none placeholder:text-fg-subtle"
                />
              </div>

              {/* Acciones masivas */}
              <div className="flex items-center justify-between gap-2 px-3 py-2 bg-bg-subtle border-b border-border flex-wrap">
                <label className="inline-flex items-center gap-2 text-sm text-fg cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    disabled={selectableVisible.length === 0}
                    className="w-4 h-4 accent-brand-500"
                  />
                  <span className="inline-flex items-center gap-1">
                    <CheckCheck className="w-4 h-4 text-fg-muted" />
                    {/* El conteo deja fuera a los sin contrato: no entran en la selección masiva. */}
                    {companyQuery || onlySelected
                      ? t('Seleccionar visibles ({{n}})', { n: selectableVisible.length })
                      : t('Seleccionar todos ({{n}})', { n: selectableEligibleCount })}
                  </span>
                </label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setOnlySelected((v) => !v)}
                    className={`text-xs font-medium ${onlySelected ? 'text-brand-600' : 'text-fg-muted hover:text-fg'}`}
                  >
                    {onlySelected ? t('Ver todos') : t('Ver seleccionados')}
                  </button>
                  {form.selection.length > 0 && (
                    <button
                      type="button"
                      onClick={clearSelection}
                      className="inline-flex items-center gap-1 text-xs font-medium text-fg-muted hover:text-status-error"
                    >
                      <Eraser className="w-3.5 h-3.5" /> {t('Limpiar')}
                    </button>
                  )}
                </div>
              </div>

              {/* Lista de holdings y comercios sueltos; los sin contrato, en su grupo al final */}
              <div className="max-h-72 overflow-y-auto divide-y divide-border">
                {visibleUnits.length === 0 ? (
                  <div className="bill-empty" style={{ padding: '2rem 1rem' }}>
                    {units.length === 0
                      ? billableReady
                        ? t('No hay comercios con contrato activo ni pedidos por cobrar en el período.')
                        : t('No hay comercios con contrato activo.')
                      : eligibleUnits.length === 0
                        ? t('Ningún comercio se puede facturar con este período.')
                        : t('Ningún comercio coincide con la búsqueda.')}
                  </div>
                ) : (
                  <>
                    {selectableVisible.map(renderUnitRow)}
                    {uncontractedVisible.length > 0 && (
                      <div role="group" aria-labelledby={uncontractedGroupId} className="divide-y divide-border">
                        <div className="flex items-start gap-2 bg-bg-subtle px-3 py-2.5 text-xs text-fg-muted">
                          <AlertTriangle className="w-4 h-4 shrink-0 mt-px text-status-error" aria-hidden />
                          <span className="min-w-0">
                            <span id={uncontractedGroupId} className="font-semibold text-fg">
                              {t('Sin contrato con el operador')}
                            </span>
                            {' · '}
                            {t('Tienen pedidos por cobrar en el período, pero ningún contrato: se cobran con la tarifa base del operador. Elígelos uno a uno; no entran en «Seleccionar todos».')}
                          </span>
                        </div>
                        {uncontractedVisible.map(renderUnitRow)}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          <p className="bill-hint" style={{ marginTop: '0.625rem' }}>
            {t('Se genera una nota de cobro independiente por cada comercio, por el periodo y modo elegidos.')}
            {holdingUnits.length > 0 && (
              <>
                {' '}
                {t('Cada holding recibe una sola nota consolidada con el detalle de todas sus empresas.')}
              </>
            )}
          </p>
        </>
      )}

      {/* ── Paso 3: preview ── */}
      {step === 3 && (
        <>
          {previewing && !preview && (
            <div className="bill-loading"><Skeleton className="h-32 w-full rounded-lg" /></div>
          )}
          {/* Al RECALCULAR (ajustes, sin tarifar, datos de un holding) el contenido queda montado
              y atenuado: si se desmontara, el bloque "Datos del holding" se cerraría y se
              perdería lo tecleado y sin guardar en sus formularios. */}
          {preview && (
            <div aria-busy={previewing} className={previewing ? 'opacity-60 transition-opacity' : undefined}>
              {previewing && (
                <p className="bill-hint" role="status" style={{ marginBottom: '0.5rem' }}>
                  {t('Recalculando la vista previa…')}
                </p>
              )}
              {/* Tras guardar datos de un holding, lo de abajo ya no es lo que se emitiría:
                  "Confirmar" queda deshabilitado hasta recalcular. */}
              {previewStale && (
                <div
                  role="status"
                  className="bill-recap"
                  style={{ background: 'rgb(var(--status-warning-soft))', marginBottom: '1rem' }}
                >
                  <div className="bill-recap__title" style={{ color: 'rgb(var(--status-warning))', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <AlertTriangle className="w-4 h-4" aria-hidden />
                    {t('Guardaste datos de un holding: recalculá la vista previa antes de confirmar.')}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => recalc()} disabled={previewing}>
                    {t('Recalcular la vista previa')}
                  </Button>
                </div>
              )}

              {/* Comercios sueltos. Con una tanda de solo holdings la grilla sobra. */}
              {(preview.perCompany.length > 0 || perHolding.length === 0) && (
                <div className="bill-card bill-card--clip" style={{ marginBottom: '1rem' }}>
                  <div className="bill-grid" role="table" style={{ '--bill-cols': '1.6fr 0.8fr 1fr' }}>
                    <div className="bill-grid__head" role="row">
                      <span className="bill-grid__hcell" role="columnheader">{t('Comercio')}</span>
                      <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t('Paquetes')}</span>
                      <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t('Total')}</span>
                    </div>
                    {preview.perCompany.map((c) => {
                      const label = clientName(c.companyId) ?? c.companyId;
                      // Con qué contrato se tarifó (o la tarifa base); un billing anterior sin
                      // `contractSource` cae a `hasContract`.
                      const contractBadge = previewContractBadge(c);
                      return (
                        <div className="bill-grid__row" role="row" key={c.companyId}>
                          <span role="cell">
                            <span className="bill-td-strong">{label}</span>
                            {contractBadge && (
                              <span className={`bill-badge bill-badge--${contractBadge.tone} bill-badge--sm ms-2`}>
                                {t(contractBadge.label)}
                              </span>
                            )}
                            {c.taxMode === 'inclusive' && <span className="bill-badge bill-badge--info bill-badge--sm ms-2">{t('IVA incl.')}</span>}
                            {c.taxMode === 'exclusive' && <span className="bill-badge bill-badge--info bill-badge--sm ms-2">{t('+ IVA')}</span>}
                            {c.unpricedCount > 0 && (
                              <span className="bill-hint d-block">{t('{{n}} sin tarifar', { n: c.unpricedCount })}</span>
                            )}
                            {c.oversized?.count > 0 && (
                              <>
                                <span className="bill-hint d-block">
                                  {c.oversized.surchargeAmount > 0
                                    ? t('{{n}} sobredimensionados (+{{amount}})', {
                                        n: c.oversized.count,
                                        amount: formatCurrency(c.oversized.surchargeAmount, c.currency),
                                      })
                                    : t('{{n}} sobredimensionados (sin recargo)', { n: c.oversized.count })}
                                </span>
                                {c.oversized.byCategory?.length > 0 && (
                                  <span className="bill-hint d-block">
                                    {formatOversizedCategories(c.oversized.byCategory, c.currency)}
                                  </span>
                                )}
                              </>
                            )}
                            {c.returned?.packagesCount > 0 && (
                              <span className="bill-hint d-block">
                                {t('{{n}} devueltos (−{{amount}})', {
                                  n: c.returned.packagesCount,
                                  amount: formatCurrency(c.returned.amount, c.currency),
                                })}
                              </span>
                            )}
                            {c.modeWarnings?.billedInOtherMode > 0 && (
                              <span className="bill-hint d-block">
                                {chargingCollected
                                  ? t('{{n}} ya cobrados por entrega (quedan fuera)', {
                                      n: c.modeWarnings.billedInOtherMode,
                                    })
                                  : t('{{n}} ya cobrados por recolección (quedan fuera)', {
                                      n: c.modeWarnings.billedInOtherMode,
                                    })}
                              </span>
                            )}
                            {c.modeWarnings?.unbilledBeforePeriod > 0 && (
                              <span className="bill-hint d-block text-status-warning">
                                {t('{{n}} sin cobrar con fecha anterior al período', {
                                  n: c.modeWarnings.unbilledBeforePeriod,
                                })}
                              </span>
                            )}
                            {/* Tarifa escalonada: de dónde salió el precio unitario. Un tramo
                                aplicado sin explicación deja al operador sin poder justificar
                                el número frente al comercio. */}
                            {c.volumeScale?.tier && (
                              <span className="bill-hint d-block">
                                {t('{{volume}} en el ciclo → tramo desde {{minQty}}: {{price}} c/u', {
                                  volume: c.volumeScale.volume,
                                  minQty: c.volumeScale.tier.minQty,
                                  price: formatCurrency(c.volumeScale.tier.unitPrice, c.currency),
                                })}
                              </span>
                            )}
                            {c.volumeScale?.cycleMismatch && (
                              <span className="bill-hint d-block text-status-error">
                                {t('No se puede emitir: se factura por ciclo {{cycle}} ({{from}} a {{to}})', {
                                  cycle:
                                    c.volumeScale.scale === 'weekly'
                                      ? t('semanal')
                                      : t('mensual'),
                                  from: c.volumeScale.expectedCycle?.start,
                                  to: c.volumeScale.expectedCycle?.end,
                                })}
                              </span>
                            )}
                            {/* Importes en cero por el tope de líneas, no por falta de pedidos. */}
                            {c.tooManyLines && (
                              <span className="bill-hint d-block text-status-error">
                                {t('No se puede emitir: supera el máximo de líneas por nota; emití por un período más corto.')}
                              </span>
                            )}
                          </span>
                          <span className="bill-grid__cell--end bill-td-muted" role="cell">{c.packageCount}</span>
                          <span className="bill-grid__cell--end bill-td-amount" role="cell">{formatCurrency(c.totalAmount, c.currency)}</span>
                        </div>
                      );
                    })}
                  </div>
                  {perHolding.length === 0 && (
                    <div className="bill-pagination" style={{ justifyContent: 'space-between' }}>
                      <span className="bill-td-strong">{t('{{n}} comercios', { n: preview.companyCount })}</span>
                      <span className="bill-td-amount" style={{ fontSize: '1rem' }}>{formatCurrency(preview.grandTotal, preview.currency)}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Holdings: una nota consolidada cada uno, con su propio bloque (bloqueos, datos
                  que faltan y detalle por empresa). El total de abajo es el de la tanda. */}
              {perHolding.length > 0 && (
                <>
                  {perHolding.map((h) => (
                    <HoldingPreviewCard
                      key={h.holdingId}
                      holding={h}
                      fiscalHolding={holdings.find((x) => String(x.id) === String(h.holdingId)) ?? null}
                      chargingCollected={chargingCollected}
                      companyNameOf={memberName}
                      onFiscalSaved={handleHoldingFiscalSaved}
                      onProfileSaved={handleHoldingProfileSaved}
                      onRecalculate={() => recalc()}
                      recalculating={previewing}
                    />
                  ))}
                  <div className="bill-card" style={{ marginBottom: '1rem' }}>
                    <div className="bill-pagination" style={{ justifyContent: 'space-between' }}>
                      <span className="bill-td-strong">
                        {t('{{n}} comercios · {{h}} holdings', {
                          n: preview.companyCount,
                          h: perHolding.length,
                        })}
                      </span>
                      <span className="bill-td-amount" style={{ fontSize: '1rem' }}>
                        {formatCurrency(preview.grandTotal, preview.currency)}
                      </span>
                    </div>
                  </div>
                </>
              )}

              {/* Comercios elegidos que billing NO tarifa solos. Sin este aviso desaparecen de
                  la vista previa sin explicación (p. ej. si no cargaron los holdings). */}
              {absorbedSkips.length > 0 && (
                <div className="bill-recap" style={{ marginBottom: '1rem' }}>
                  <div className="bill-recap__title" style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <Network className="w-4 h-4" aria-hidden />
                    {t('{{n}} comercio(s) se cobran dentro de su holding', { n: absorbedSkips.length })}
                  </div>
                  <p className="bill-hint mb-0">
                    {absorbedSkips
                      .map((s) => {
                        const holding = holdingName(s.holdingIds?.[0]);
                        const company = clientName(s.companyId) ?? s.companyId;
                        return holding ? `${company} (${holding})` : company;
                      })
                      .join(', ')}
                    {'. '}
                    {t('No van en esta tanda como comercios sueltos: elegí su holding para cobrarlos.')}
                  </p>
                </div>
              )}
              {conflictSkips.length > 0 && (
                <div className="bill-recap" style={{ background: 'rgb(var(--status-warning-soft))', marginBottom: '1rem' }}>
                  <div className="bill-recap__title" style={{ color: 'rgb(var(--status-warning))', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <AlertTriangle className="w-4 h-4" aria-hidden />
                    {t('{{n}} comercio(s) están en más de un holding', { n: conflictSkips.length })}
                  </div>
                  <p className="bill-hint mb-0">
                    {conflictSkips.map((s) => clientName(s.companyId) ?? s.companyId).join(', ')}
                    {'. '}
                    {t('No se cobran hasta que se resuelva a qué holding pertenecen: revisalo en la ficha de cada holding (Empresas → Holdings).')}
                  </p>
                </div>
              )}
              {skippedHoldingsInPreview.length > 0 && (
                <div className="bill-recap" style={{ background: 'rgb(var(--status-warning-soft))', marginBottom: '1rem' }}>
                  <div className="bill-recap__title" style={{ color: 'rgb(var(--status-warning))', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <AlertTriangle className="w-4 h-4" aria-hidden />
                    {t('{{n}} holding(s) no se cobran en esta tanda', { n: skippedHoldingsInPreview.length })}
                  </div>
                  <p className="bill-hint mb-0">
                    {skippedHoldingsInPreview
                      .map((s) =>
                        t('{{name}} ({{reason}})', {
                          name: holdingName(s.holdingId) ?? s.holdingId,
                          reason: holdingSkipReasonText(s.reason, t),
                        }),
                      )
                      .join(', ')}
                    .
                  </p>
                </div>
              )}

              {/* El botón "Confirmar" se deshabilita con total 0. Sin este aviso, un
                  operador cuyos paquetes se descontaron enteros no tiene forma de saber
                  por qué el wizard no lo deja avanzar. */}
              {preview.grandTotal <= 0 && (
                <div className="bill-recap" style={{ marginBottom: '1rem' }}>
                  <div className="bill-recap__title" style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <AlertTriangle className="w-4 h-4" /> {t('No hay nada que cobrar')}
                  </div>
                  <p className="bill-hint mb-0">
                    {t('No queda un monto por cobrar en el periodo: los descuentos cubren todo lo facturable o los paquetes ya se cobraron. Revisá los avisos y los ajustes de abajo, o cambiá el periodo.')}
                  </p>
                </div>
              )}

              {/* Comercios que el backend va a omitir por no cubrir su ciclo. El paso 2 ya
                  los filtra, así que llegar acá significa que la selección quedó vieja o
                  que la escala del comercio cambió en el medio. Se avisa igual: sin esto,
                  el operador confirma y descubre la omisión recién en el resultado. */}
              {cycleBlocked.length > 0 && (
                <div className="bill-recap" style={{ background: 'rgb(var(--status-warning-soft))', marginBottom: '1rem' }}>
                  <div className="bill-recap__title" style={{ color: 'rgb(var(--status-warning))', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <Layers className="w-4 h-4" />
                    {t('{{n}} comercio(s) no se pueden emitir con este período', { n: cycleBlocked.length })}
                  </div>
                  <p className="bill-hint mb-0">
                    {t('Se facturan por ciclo completo (semana dom–sáb o mes calendario) porque tienen tarifa escalonada por volumen. Volvé al paso 1 y elegí el período que corresponde, o sacalos de la selección.')}
                  </p>
                </div>
              )}

              {/* Notas que superarían el máximo de líneas por nota: billing las deja en cero y
                  no las emite. Sin el aviso, un comercio con miles de pedidos y total cero
                  parece un error de tarifa. */}
              {tooManyLinesNames.length > 0 && (
                <div className="bill-recap" style={{ background: 'rgb(var(--status-warning-soft))', marginBottom: '1rem' }}>
                  <div className="bill-recap__title" style={{ color: 'rgb(var(--status-warning))', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <AlertTriangle className="w-4 h-4" aria-hidden />
                    {t('{{n}} nota(s) no se pueden emitir: superan el máximo de líneas por nota', {
                      n: tooManyLinesNames.length,
                    })}
                  </div>
                  <p className="bill-hint mb-2">
                    {t('{{names}}. Emití por un período más corto para que cada nota quede bajo el máximo.', {
                      names: tooManyLinesNames.join(', '),
                    })}
                  </p>
                  <Button variant="outline" size="sm" onClick={() => setStep(1)} iconLeft={<ArrowLeft className="w-4 h-4" />}>
                    {t('Cambiar el período')}
                  </Button>
                </div>
              )}

              {/* Hueco entre modos. Típico al pasar de "por entrega" a "por recolección": lo
                  recolectado antes del período y entregado dentro no entró en la nota por
                  entrega anterior ni entra en esta. Nada más lo delata, así que sin este
                  aviso esos paquetes quedan sin cobrar en silencio. */}
              {modeWarningTotals.unbilledBeforePeriod > 0 && (
                <div className="bill-recap" style={{ background: 'rgb(var(--status-warning-soft))', marginBottom: '1rem' }}>
                  <div className="bill-recap__title" style={{ color: 'rgb(var(--status-warning))', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <AlertTriangle className="w-4 h-4" />
                    {chargingCollected
                      ? t('{{n}} paquete(s) recolectado(s) antes del período siguen sin cobrar', {
                          n: modeWarningTotals.unbilledBeforePeriod,
                        })
                      : t('{{n}} paquete(s) entregado(s) antes del período siguen sin cobrar', {
                          n: modeWarningTotals.unbilledBeforePeriod,
                        })}
                  </div>
                  <p className="bill-hint mb-2">
                    {chargingCollected
                      ? t('Se recolectaron en los {{days}} días anteriores al {{start}} y no se cobraron en ningún modo, así que ninguna nota de este período los va a incluir. Suele pasar al cambiar de "por entrega" a "por recolección".', {
                          days: MODE_GAP_LOOKBACK_DAYS,
                          start: resolvedRange.start,
                        })
                      : t('Se entregaron en los {{days}} días anteriores al {{start}} y no se cobraron en ningún modo, así que ninguna nota de este período los va a incluir.', {
                          days: MODE_GAP_LOOKBACK_DAYS,
                          start: resolvedRange.start,
                        })}
                  </p>
                  <p className="bill-hint mb-2">
                    {t('Si corresponde cobrarlos, ampliá el período para que empiece antes o emití la nota del período anterior.')}
                  </p>
                  <Button variant="outline" size="sm" onClick={() => setStep(1)} iconLeft={<ArrowLeft className="w-4 h-4" />}>
                    {t('Cambiar el período')}
                  </Button>
                </div>
              )}

              {/* Paquetes del período que billing deja fuera porque ya se cobraron en el otro
                  modo. Sin el aviso, la diferencia con lo que el operador espera cobrar parece
                  un error de la nota. */}
              {modeWarningTotals.billedInOtherMode > 0 && (
                <div className="bill-recap" style={{ marginBottom: '1rem' }}>
                  <div className="bill-recap__title" style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <Info className="w-4 h-4" />
                    {chargingCollected
                      ? t('{{n}} paquete(s) ya cobrado(s) por entrega quedan fuera', {
                          n: modeWarningTotals.billedInOtherMode,
                        })
                      : t('{{n}} paquete(s) ya cobrado(s) por recolección quedan fuera', {
                          n: modeWarningTotals.billedInOtherMode,
                        })}
                  </div>
                  <p className="bill-hint mb-0">
                    {chargingCollected
                      ? t('Ya están en una nota "por entrega". Un paquete se cobra una sola vez y en un solo modo, así que no se suman a esta.')
                      : t('Ya están en una nota "por recolección". Un paquete se cobra una sola vez y en un solo modo, así que no se suman a esta.')}
                  </p>
                </div>
              )}

              {/* Resumen del escalonado de la tanda: qué precio unitario quedó y por qué.
                  Es la única forma de justificar el número frente al comercio. */}
              {tieredCompanies.length > 0 && (
                <div className="bill-recap" style={{ marginBottom: '1rem' }}>
                  <div className="bill-recap__title" style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <Layers className="w-4 h-4" />
                    {t('{{n}} comercio(s) con tarifa escalonada', { n: tieredCompanies.length })}
                  </div>
                  <p className="bill-hint mb-0">
                    {t('El precio del tramo alcanzado se aplica a todos los pedidos del ciclo. Los pedidos con tarifa propia por comuna o código postal conservan la suya.')}
                  </p>
                </div>
              )}

              {totalUnpriced > 0 && (
                <div className="bill-recap" style={{ background: 'rgb(var(--status-warning-soft))', marginBottom: '1rem' }}>
                  <div className="bill-recap__title" style={{ color: 'rgb(var(--status-warning))', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <AlertTriangle className="w-4 h-4" /> {t('{{n}} paquete(s) sin tarifar', { n: totalUnpriced })}
                  </div>
                  <p className="bill-hint mb-2">
                    {t('Hay paquetes en comunas sin precio configurado. No se cobrarán salvo que les asignes un precio acá (o configures la tarifa de esas comunas en el contrato).')}
                  </p>
                  <div className="d-flex gap-2 align-items-end flex-wrap">
                    <div className="bill-field" style={{ maxWidth: 220 }}>
                      <label className="bill-label">{t('Precio para los sin tarifar')} ({currency})</label>
                      <Input type="number" min="0" name="unpricedFallbackPrice" value={form.unpricedFallbackPrice} onChange={handleChange} />
                    </div>
                    <Button variant="outline" onClick={() => recalc()} disabled={previewing}>{t('Recalcular')}</Button>
                  </div>
                </div>
              )}

              {/* Sobredimensionados cobrados a precio normal por falta de recargo.
                  No bloquea: avisa. A diferencia de los "sin tarifar", que se ven porque
                  no suman, éstos suman lo mismo que un paquete normal y nada los delata
                  en la nota. */}
              {oversizedGap.count > 0 && (
                <div className="bill-recap" style={{ background: 'rgb(var(--status-warning-soft))', marginBottom: '1rem' }}>
                  <div className="bill-recap__title" style={{ color: 'rgb(var(--status-warning))', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <PackageOpen className="w-4 h-4" />
                    {t('{{n}} sobredimensionado(s) sin recargo configurado', { n: oversizedGap.count })}
                  </div>
                  <p className="bill-hint mb-2">
                    {t('Se están cobrando al precio normal de su comuna. Configurá el recargo en Configuración → Cobro a comercios (o en la tarifa del contrato) y volvé a calcular.')}
                  </p>
                  <div className="d-flex gap-2 flex-wrap">
                    {oversizedGap.byCategory.map((g) => (
                      <span key={g.category} className="bill-badge bill-badge--warning bill-badge--sm">
                        {g.category}: {g.count}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Pedidos devueltos (estados 21, 22, 31 y 41). Billing los descuenta siempre y
                  en los dos modos: van como línea de la nota más un ajuste de descuento. El
                  bloque es informativo (no hay nada que elegir), pero sin él el total no se
                  explica desde la pantalla. */}
              {returnedTotals.count > 0 && (
                <div className="bill-recap" style={{ marginBottom: '1rem' }}>
                  <div className="d-flex align-items-start gap-2">
                    <span className="flex-grow-1">
                      <span className="bill-recap__title" style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.25rem' }}>
                        <Undo2 className="w-4 h-4" aria-hidden />
                        {t('{{n}} pedido(s) devuelto(s): no se cobran', { n: returnedTotals.count })}
                      </span>
                      <span className="bill-hint d-block">
                        {t('Están en devolución (recibidos para devolución, en bodega o en ruta de devolución) o con la devolución completada. Van en la nota como línea y se descuentan, en los dos modos de cobro.')}
                      </span>
                      {/* En "entregado" el único devuelto posible es una entrega real que el
                          cliente devolvió después: es lo que más sorprende, así que se nombra. */}
                      {form.chargeMode === 'entregado' && (
                        <span className="bill-hint d-block">
                          {t('Por lo entregado, son entregas del período que después pasaron a devolución.')}
                        </span>
                      )}
                      {returnedTotals.unpriced > 0 && (
                        <span className="bill-hint d-block">
                          {t('{{n}} de ellos están sin tarifar, así que no suman al descuento (nunca entraron al subtotal).', { n: returnedTotals.unpriced })}
                        </span>
                      )}
                    </span>
                    {returnedTotals.amount > 0 && (
                      <span className="bill-td-neg">−{formatCurrency(returnedTotals.amount, currency)}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Ajustes manuales */}
              <div className="bill-field" style={{ marginBottom: '0.75rem' }}>
                <label className="bill-label">{t('Ajustes manuales (recargos / descuentos)')}</label>
              </div>
              {form.adjustments.length > 0 && (
                <ul className="list-unstyled" style={{ marginBottom: '0.75rem' }}>
                  {form.adjustments.map((a, i) => (
                    <li key={i} className="d-flex align-items-center gap-2" style={{ marginBottom: '0.375rem' }}>
                      <span className={`bill-badge bill-badge--sm bill-badge--${a.type === 'surcharge' ? 'warning' : 'success'}`}>
                        {a.type === 'surcharge' ? t('Recargo') : t('Descuento')}
                      </span>
                      <span className="flex-grow-1 bill-td-muted">{adjustmentUnitLabel(a)} — {a.label}</span>
                      <span className={a.type === 'discount' ? 'bill-td-neg' : 'bill-td-amount'}>
                        {a.type === 'discount' ? '−' : '+'}{formatCurrency(a.amount, currency)}
                      </span>
                      <button type="button" className="bill-inline__btn bill-inline__btn--cancel" onClick={() => removeAdjustment(i)} disabled={previewing} title={t('Quitar')}>
                        <X className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="d-flex gap-2 flex-wrap align-items-end">
                <div className="bill-field" style={{ minWidth: 150, flex: 1 }}>
                  <label className="bill-label">
                    {selectedHoldingCount > 0 ? t('Comercio u holding') : t('Comercio')}
                  </label>
                  <Select value={adjForm.unit} onChange={(e) => setAdjForm((a) => ({ ...a, unit: e.target.value }))}>
                    <option value="">{t('Selecciona…')}</option>
                    {form.selection.map((u) => (
                      <option key={unitKey(u)} value={unitKey(u)}>
                        {u.kind === UNIT_HOLDING ? t('{{name}} (holding)', { name: u.label }) : u.label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="bill-field" style={{ minWidth: 120 }}>
                  <label className="bill-label">{t('Tipo')}</label>
                  <Select value={adjForm.type} onChange={(e) => setAdjForm((a) => ({ ...a, type: e.target.value }))}>
                    <option value="surcharge">{t('Recargo')}</option>
                    <option value="discount">{t('Descuento')}</option>
                  </Select>
                </div>
                <div className="bill-field" style={{ minWidth: 140, flex: 1 }}>
                  <label className="bill-label">{t('Concepto')}</label>
                  <Input value={adjForm.label} onChange={(e) => setAdjForm((a) => ({ ...a, label: e.target.value }))} placeholder={t('Ej: Combustible, ajuste comercial…')} />
                </div>
                <div className="bill-field" style={{ maxWidth: 130 }}>
                  <label className="bill-label">{t('Monto')} ({currency})</label>
                  <Input type="number" min="0" value={adjForm.amount} onChange={(e) => setAdjForm((a) => ({ ...a, amount: e.target.value }))} />
                </div>
                <Button variant="outline" onClick={addAdjustment} disabled={previewing} iconLeft={<Plus className="w-4 h-4" />}>{t('Agregar')}</Button>
              </div>

              <div className="bill-field" style={{ marginTop: '1rem' }}>
                <label className="bill-label">{t('Notas internas (opcional)')}</label>
                <textarea className="bill-textarea" name="notes" rows={2} value={form.notes} onChange={handleChange} />
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Paso 4: confirmación ── */}
      {step === 4 && preview && (
        <div className="bill-recap">
          <div className="bill-recap__title" style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <AlertTriangle className="w-4 h-4" /> {t('Confirmación final')}
          </div>
          <p className="mb-2">
            {t('Vas a')}{' '}
            <strong>{t('generar {{n}} nota(s) de cobro', { n: noteCount })}</strong>{' '}
            {t('por un total de')}{' '}
            <strong>{formatCurrency(preview.grandTotal, preview.currency)}</strong>{' '}
            ({t(CHARGE_MODE_LABELS[form.chargeMode])}, {resolvedRange.start} → {resolvedRange.end}).
          </p>
          {perHolding.length > blockedHoldings.length && (
            <p className="mb-2">
              {t('Incluye {{n}} nota(s) consolidada(s) de holding, a nombre del holding y con el detalle por empresa.', {
                n: perHolding.length - blockedHoldings.length,
              })}
            </p>
          )}
          {/* Un holding bloqueado (o que billing dejó fuera) no se emite: se nombra antes de
              confirmar para que no se dé por cobrado. */}
          {notEmittedHoldingNames.length > 0 && (
            <p className="mb-2">
              {t('No se va a emitir la nota de {{n}} holding(s) bloqueado(s): {{names}}.', {
                n: notEmittedHoldingNames.length,
                names: notEmittedHoldingNames.join(', '),
              })}
            </p>
          )}
          {tooManyLinesNames.length > 0 && (
            <p className="mb-2">
              {t('No se van a emitir {{n}} nota(s) que superan el máximo de líneas por nota ({{names}}): emití por un período más corto.', {
                n: tooManyLinesNames.length,
                names: tooManyLinesNames.join(', '),
              })}
            </p>
          )}
          {oversizedTotals.count > 0 && oversizedTotals.amount > 0 && (
            <>
              <p className="mb-2">
                {t('Incluye {{amount}} de recargo por {{n}} pedido(s) sobredimensionado(s).', {
                  amount: formatCurrency(oversizedTotals.amount, preview.currency),
                  n: oversizedTotals.count,
                })}
              </p>
              {oversizedTotals.byCategory.length > 0 && (
                <p className="bill-hint mb-2">
                  {formatOversizedCategories(oversizedTotals.byCategory, preview.currency)}
                </p>
              )}
            </>
          )}
          {returnedTotals.count > 0 && returnedTotals.amount > 0 && (
            <p className="mb-2">
              {t('Incluye un descuento de {{amount}} por {{n}} pedido(s) devuelto(s).', {
                amount: formatCurrency(returnedTotals.amount, preview.currency),
                n: returnedTotals.count,
              })}
            </p>
          )}
          {/* Último recordatorio del hueco antes de una acción irreversible: emitir no lo
              arregla, y después de este paso el aviso del paso 3 ya no se ve. */}
          {modeWarningTotals.unbilledBeforePeriod > 0 && (
            <p className="mb-2">
              {t('Atención: {{n}} paquete(s) con fecha anterior al período siguen sin cobrar y estas notas no los incluyen.', {
                n: modeWarningTotals.unbilledBeforePeriod,
              })}
            </p>
          )}
          <p className="bill-hint mb-0">
            {t('Esta acción es irreversible: los paquetes incluidos quedarán marcados como cobrados y ya no van a entrar en ninguna otra nota, de ningún modo. ¿Confirmas?')}
          </p>
        </div>
      )}
    </Modal>
  );
};

export default GenerateCobroWizard;
